import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  CircularProgress,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import { getAnalyticsSummary } from '../services/analyticsService';
import { getAllCustomers } from '../services/customerService';

function AdminAnalytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [customerCount, setCustomerCount] = useState(0);
  const [contactsToday, setContactsToday] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [analyticsData, customers] = await Promise.all([
        getAnalyticsSummary(),
        getAllCustomers()
      ]);
      setAnalytics(analyticsData);
      setCustomerCount(customers.length);

      const today = new Date().toDateString();
      setContactsToday(
        customers.filter(c => new Date(c.createdAt).toDateString() === today).length
      );
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const funnelData = [
    { label: 'Scansioni', value: analytics?.scansWeek || 0, color: '#2196F3' },
    { label: 'Try-On', value: analytics?.tryonsWeek || 0, color: '#667eea' },
    { label: 'Contatti', value: contactsToday, color: '#4CAF50' }
  ];

  const maxFunnel = Math.max(...funnelData.map(d => d.value), 1);

  const BAR_COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4CAF50'];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ backgroundColor: '#1a1a1a' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/admin')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Analytics
          </Typography>
          <Chip icon={<TrendingUpIcon />} label="Live" color="success" size="small" />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Main Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper elevation={2} sx={{ p: 2.5, textAlign: 'center', borderRadius: '16px' }}>
              <QrCodeScannerIcon sx={{ fontSize: 32, color: '#2196F3', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{analytics?.scansToday || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Scansioni Oggi</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper elevation={2} sx={{ p: 2.5, textAlign: 'center', borderRadius: '16px' }}>
              <QrCodeScannerIcon sx={{ fontSize: 32, color: '#9C27B0', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{analytics?.scansWeek || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Scansioni Settimana</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper elevation={2} sx={{ p: 2.5, textAlign: 'center', borderRadius: '16px' }}>
              <CheckroomIcon sx={{ fontSize: 32, color: '#667eea', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{analytics?.tryonsToday || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Try-On Oggi</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper elevation={2} sx={{ p: 2.5, textAlign: 'center', borderRadius: '16px' }}>
              <PeopleIcon sx={{ fontSize: 32, color: '#4CAF50', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{contactsToday}</Typography>
              <Typography variant="body2" color="text.secondary">Contatti Oggi</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Top 5 Products */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: '16px', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <StarIcon sx={{ color: '#FF9800' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Top Prodotti Scansionati
                </Typography>
              </Box>

              {(analytics?.topProducts || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Nessun dato disponibile. Le statistiche appariranno dopo le prime scansioni.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {(analytics?.topProducts || []).slice(0, 5).map((product, index) => (
                    <Box key={product.productId} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '13px',
                          fontWeight: 700,
                          backgroundColor: index === 0 ? '#FFF3E0' : '#f5f5f5',
                          color: index === 0 ? '#FF9800' : '#666',
                          flexShrink: 0
                        }}
                      >
                        {index + 1}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {product.productName}
                        </Typography>
                        <Box sx={{ mt: 0.5, height: 6, borderRadius: 3, backgroundColor: '#f0f0f0', overflow: 'hidden' }}>
                          <Box
                            sx={{
                              height: '100%',
                              borderRadius: 3,
                              width: `${(product.count / (analytics.topProducts[0]?.count || 1)) * 100}%`,
                              background: `linear-gradient(90deg, ${BAR_COLORS[index % BAR_COLORS.length]}, ${BAR_COLORS[(index + 1) % BAR_COLORS.length]})`
                            }}
                          />
                        </Box>
                      </Box>
                      <Chip label={product.count} size="small" sx={{ fontWeight: 700, minWidth: 40 }} />
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Conversion Funnel */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: '16px', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#667eea' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Funnel Conversione (Settimana)
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
                {funnelData.map((step, index) => (
                  <Box key={step.label}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{step.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{step.value}</Typography>
                    </Box>
                    <Box sx={{ height: 24, borderRadius: 12, backgroundColor: '#f0f0f0', overflow: 'hidden' }}>
                      <Box
                        sx={{
                          height: '100%',
                          borderRadius: 12,
                          width: `${Math.max((step.value / maxFunnel) * 100, step.value > 0 ? 8 : 0)}%`,
                          backgroundColor: step.color,
                          transition: 'width 0.8s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {step.value > 0 && (
                          <Typography sx={{ color: 'white', fontSize: '11px', fontWeight: 700 }}>
                            {step.value}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {index < funnelData.length - 1 && funnelData[index].value > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                        {Math.round((funnelData[index + 1].value / funnelData[index].value) * 100) || 0}% conversione
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Monthly Overview */}
          <Grid size={{ xs: 12 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: '16px' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Riepilogo Mensile
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#2196F3' }}>
                      {analytics?.scansMonth || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Scansioni Totali</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#667eea' }}>
                      {analytics?.tryonsWeek || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Try-On Settimana</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                      {customerCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Clienti Totali</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#FF9800' }}>
                      {(analytics?.topProducts || []).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Prodotti Attivi</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default AdminAnalytics;
